import React, { useState } from "react";
import api from "src/store/features/api";
import {
  StyledContentHightLight,
  StyledDivider,
  StyledLabel,
  StyledTitle,
} from "./styled-components/TableStyledComponents";
import { Pagination, Stack } from "@mui/material";
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import { dynamicPriceFormatter, formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";
import { DcaStrategyDto } from "src/store/features/api/generated";
import { formatStatus } from "./utils/tools";
import { LoadingDotsWithText } from "./styled-components/LoadingDots";

export default function OrderDetails({ strategy }: { strategy: DcaStrategyDto }) {
  const [page, setPage] = useState(1);

  const {
    _id,
    status,
    buyCurrency: { logoUrl },
    sellCurrency: { code: sellToken },
    nextOrderDueDate,
    pastTriggers,
    totalTriggers,
    endDate,
  } = strategy;

  const { data: { triggers: ordersDetails, pageCount } = {}, isFetching } =
    api.useGetUserDcaTriggersQuery({
      strategyId: _id,
      page,
    });

  const calculateRemainingOrders = () => {
    if (totalTriggers && pastTriggers) {
      return JSON.stringify(totalTriggers - pastTriggers);
    } else {
      return "-";
    }
  };

  return (
    <>
      {/* Orders Tab */}
      <div>
        {/* Header */}
        <StyledContentHightLight style={{ marginBottom: "16px" }}>
          {isFetching ? (
            <div style={{ marginBottom: "44px" }}>
              <LoadingDotsWithText text="Loading your orders" />
            </div>
          ) : (
            <>
              {[
                {
                  label:
                    status === "Cancelled" ? "Your cancelled order(s)" : "Your remaining order(s)",
                  value: calculateRemainingOrders(),
                },
                { label: "Completed order(s)", value: pastTriggers },
                { label: "End Date", value: endDate ? dateFormatter(endDate) : "-" },
              ].map(({ label, value }) => (
                <Stack direction="row" justifyContent="space-between">
                  <StyledLabel style={{ marginBottom: "4px" }}>{label}</StyledLabel>
                  <StyledLabel sx={{ color: "black" }}>{value}</StyledLabel>
                </Stack>
              ))}
            </>
          )}
        </StyledContentHightLight>

        {ordersDetails && (
          <>
            {" "}
            {/* Message displayed when orders are not available */}
            {ordersDetails.length === 0 && (
              <div style={{ margin: "20px 0px" }}>
                <StyledLabel>{`Your orders will appear here after it executes, your next scheduled order is ${dateFormatter(
                  nextOrderDueDate,
                )}`}</StyledLabel>
              </div>
            )}
            {/* Orders */}
            {ordersDetails.map((order) => (
              <div style={{ marginTop: "40px" }}>
                <div style={{ margin: "0px 4px" }}>
                  <StyledLabel>{dateFormatter(+order.timeToExecute)}</StyledLabel>
                  <StyledDivider style={{ margin: "10px 0px" }} />
                </div>
                <Stack direction="row" justifyContent="space-between" m={"4px -4px 4px 4px"}>
                  <StyledTitle>{`${order.sellCurrency.code} → ${order.buyCurrency.code}`}</StyledTitle>
                  <Stack direction="row" gap={1}>
                    <StyledTitle>{`${order.buyUnits ?? "-"}`}</StyledTitle>
                    <img
                      alt="crypto logo"
                      src={logoUrl}
                      style={{ width: "24px", height: "24px" }}
                    />
                  </Stack>
                </Stack>
                {[
                  { label: "Amount", value: formatCurrency(order.sellUnits, sellToken) },
                  {
                    label: `${order.buyCurrency.code} Price`,
                    value:
                      dynamicPriceFormatter(order.buyCurrencyPrice, order.sellCurrency.code) ?? "-",
                  },
                  { label: "Status", value: formatStatus(order.status) },
                  { label: "Fee", value: order.fee },
                ].map(({ label, value }) => (
                  <Stack direction="row" justifyContent="space-between" m={"8px 4px"}>
                    <StyledLabel>{label}</StyledLabel>
                    <StyledLabel>{value}</StyledLabel>
                  </Stack>
                ))}
              </div>
            ))}
          </>
        )}

        {/* Pagination */}
        {pageCount && pageCount > 1 ? (
          <div style={{ display: "flex", justifyContent: "center", margin: "20px 0px" }}>
            <Pagination count={pageCount} page={page} onChange={(_, n) => setPage(n)} />
          </div>
        ) : null}
      </div>
    </>
  );
}
